import AppGrid from "@/features/order-sheet-sets/app-grid";
import GridRowIndicator from "@/features/order-sheet-sets/grid-row-indicator";
import useInvoiceGrid from "@/features/invoices/hooks/use-invoice-grid";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import { DenseProformaInvoice } from "@/features/invoices/proforma-invoices/proforma-invoice.type";
import { useEffect, useMemo, useState } from "react";
import { ProformaInvoiceRow } from "@/features/invoices/proforma-invoices/proforma-invoice-row.type";
import { proformaInvoiceRowMapper } from "@/features/invoices/utils/proforma-invoice-row-mapper";
import useIdentity from "@/features/ui/hooks/use-identity";

interface AgencyProformaInvoiceGridProps {
  resources?: DenseProformaInvoice[];
  mutateResources: () => void;
}

export default function ProformaInvoiceGrid({
  resources,
  mutateResources,
}: AgencyProformaInvoiceGridProps) {
  const identity = useIdentity();
  const [rows, setRows] = useState<ProformaInvoiceRow[]>();
  const rowMapper = useMemo(() => {
    return proformaInvoiceRowMapper.copy();
  }, []);

  useEffect(() => {
    setRows((prev) => {
      return rowMapper.toRows(
        identity?.company?.type !== "BUYER"
          ? resources?.map((resource) => {
              return {
                ...resource,
                isRead: false,
              };
            })
          : resources,
        prev
      );
    });
  }, [identity?.company?.type, resources, rowMapper]);

  const invoiceGrid = useInvoiceGrid({
    rows,
    mutateRows: mutateResources,
  });

  const { navigate } = useRouterHelper();

  switch (identity?.company?.type) {
    case "AGENCY":
      return (
        <AppGrid
          rows={rows}
          metas={[
            {
              gap: "8px",
              rowType: "ProformaInvoice",
              columns: [
                invoiceGrid.pairs.season.column,
                invoiceGrid.pairs.orderedBy.column,
                // invoiceGrid.pairs.issuedThrough.column,
                invoiceGrid.pairs.issuedBy.column,
                invoiceGrid.pairs.title.column,
                invoiceGrid.pairs.totalAmount.column,
                invoiceGrid.pairs.status.column,
                invoiceGrid.pairs.remittanceStatus.column,
                invoiceGrid.pairs.exposedToBuyer.column,
                invoiceGrid.pairs.paidBy.column,
                invoiceGrid.pairs.paymentMethod.column,
                invoiceGrid.pairs.issuedOn.column,
              ],
              fields: [
                invoiceGrid.pairs.season.field,
                invoiceGrid.pairs.orderedBy.field,
                // invoiceGrid.pairs.issuedThrough.field,
                invoiceGrid.pairs.issuedBy.field,
                invoiceGrid.pairs.title.field,
                invoiceGrid.pairs.totalAmount.field,
                invoiceGrid.pairs.status.field,
                invoiceGrid.pairs.remittanceStatus.field,
                invoiceGrid.pairs.exposedToBuyer.field,
                invoiceGrid.pairs.paidBy.field,
                invoiceGrid.pairs.paymentMethod.field,
                invoiceGrid.pairs.issuedOn.field,
              ],
              actions: [invoiceGrid.actions.downloadInvoice],
              indicator: (row) => (
                <GridRowIndicator type={"VIEW"} isHovered={row.isHovered} />
              ),
              onClick: (row) => {
                navigate(`/proforma-invoices/${row.id}`);
              },
            },
          ]}
        />
      );
    case "BOUTIQUE":
      return (
        <AppGrid
          rows={rows}
          metas={[
            {
              gap: "8px",
              rowType: "ProformaInvoice",
              columns: [
                invoiceGrid.pairs.season.column,
                invoiceGrid.pairs.orderedBy.column,
                // invoiceGrid.pairs.issuedThrough.column,
                // invoiceGrid.pairs.issuedBy.column,
                invoiceGrid.pairs.title.column,
                invoiceGrid.pairs.totalAmount.column,
                invoiceGrid.pairs.remittanceStatus.column,
                // invoiceGrid.pairs.exposedToBuyer.column,
                // invoiceGrid.pairs.paidBy.column,
                // invoiceGrid.pairs.paymentMethod.column,
                invoiceGrid.pairs.issuedOn.column,
              ],
              fields: [
                invoiceGrid.pairs.season.field,
                invoiceGrid.pairs.orderedBy.field,
                // invoiceGrid.pairs.issuedThrough.field,
                // invoiceGrid.pairs.issuedBy.field,
                invoiceGrid.pairs.title.field,
                invoiceGrid.pairs.totalAmount.field,
                invoiceGrid.pairs.remittanceStatus.field,
                // invoiceGrid.pairs.exposedToBuyer.field,
                // invoiceGrid.pairs.paidBy.field,
                // invoiceGrid.pairs.paymentMethod.field,
                invoiceGrid.pairs.issuedOn.field,
              ],
              actions: [invoiceGrid.actions.downloadInvoice],
              indicator: (row) => (
                <GridRowIndicator type={"VIEW"} isHovered={row.isHovered} />
              ),
              onClick: (row) => {
                navigate(`/proforma-invoices/${row.id}`);
              },
            },
          ]}
        />
      );
    case "BUYER":
      return (
        <AppGrid
          rows={rows}
          metas={[
            {
              gap: "8px",
              rowType: "ProformaInvoice",
              columns: [
                invoiceGrid.pairs.season.column,
                // invoiceGrid.pairs.orderedBy.column,
                invoiceGrid.pairs.issuedThrough.column,
                invoiceGrid.pairs.issuedBy.column,
                invoiceGrid.pairs.title.column,
                invoiceGrid.pairs.totalAmount.column,
                invoiceGrid.pairs.status.column,
                invoiceGrid.pairs.remittanceStatus.column,
                // invoiceGrid.pairs.exposedToBuyer.column,
                invoiceGrid.pairs.paidBy.column,
                invoiceGrid.pairs.paymentMethod.column,
                invoiceGrid.pairs.issuedOn.column,
              ],
              fields: [
                invoiceGrid.pairs.season.field,
                // invoiceGrid.pairs.orderedBy.field,
                invoiceGrid.pairs.issuedThrough.field,
                invoiceGrid.pairs.issuedBy.field,
                invoiceGrid.pairs.title.field,
                invoiceGrid.pairs.totalAmount.field,
                invoiceGrid.pairs.status.field,
                invoiceGrid.pairs.remittanceStatus.field,
                // invoiceGrid.pairs.exposedToBuyer.field,
                invoiceGrid.pairs.paidBy.field,
                invoiceGrid.pairs.paymentMethod.field,
                invoiceGrid.pairs.issuedOn.field,
              ],
              actions: [invoiceGrid.actions.downloadInvoice],
              indicator: (row) => (
                <GridRowIndicator type={"VIEW"} isHovered={row.isHovered} />
              ),
              onClick: (row) => {
                navigate(`/proforma-invoices/${row.id}`);
              },
            },
          ]}
        />
      );
    default:
      return null;
  }
}
